import React, { useEffect, useMemo, useState } from 'react';
import { FaPause, FaPen, FaPlay } from 'react-icons/fa';

const OrderPageProduction = ({
    selectedStair,
    handleStairModification,
    handleWorkTimerToggle,
    formatTimestamp,
    onWorkTimeEdit,
}) => {
    const [currentTime, setCurrentTime] = useState(Date.now());

    const operations = useMemo(() => ([
        {
            id: 'stepCutting',
            name: 'stepCutting',
            label: 'Leikkaus',
            value: selectedStair?.stepCutting,
            timestamp: selectedStair?.stepCutting_modifiedTimestamp,
            workStartTime: selectedStair?.stepCutting_workStartTime,
            workEndTime: selectedStair?.stepCutting_workEndTime,
            workBreakDuration: selectedStair?.stepCutting_workBreakDuration,
        },
        {
            id: 'stepGluing',
            name: 'stepGluing',
            label: 'Liimaus',
            value: selectedStair?.stepGluing,
            timestamp: selectedStair?.stepGluing_modifiedTimestamp,
            workStartTime: selectedStair?.stepGluing_workStartTime,
            workEndTime: selectedStair?.stepGluing_workEndTime,
            workBreakDuration: selectedStair?.stepGluing_workBreakDuration,
        },
        {
            id: 'stepSanding',
            name: 'stepSanding',
            label: 'Hionta',
            value: selectedStair?.stepSanding,
            timestamp: selectedStair?.stepSanding_modifiedTimestamp,
            workStartTime: selectedStair?.stepSanding_workStartTime,
            workEndTime: selectedStair?.stepSanding_workEndTime,
            workBreakDuration: selectedStair?.stepSanding_workBreakDuration,
        },
        {
            id: 'handRail',
            name: 'handRail',
            label: 'Käsijohteet',
            value: selectedStair?.handRail,
            timestamp: selectedStair?.handRail_modifiedTimestamp,
            workStartTime: selectedStair?.handRail_workStartTime,
            workEndTime: selectedStair?.handRail_workEndTime,
            workBreakDuration: selectedStair?.handRail_workBreakDuration,
        },
        {
            id: 'ledStrips',
            name: 'ledStrips',
            label: 'Listat',
            value: selectedStair?.ledStrips,
            timestamp: selectedStair?.ledStrips_modifiedTimestamp,
            workStartTime: selectedStair?.ledStrips_workStartTime,
            workEndTime: selectedStair?.ledStrips_workEndTime,
            workBreakDuration: selectedStair?.ledStrips_workBreakDuration,
        },
        {
            id: 'stepPackaging',
            name: 'stepPackaging',
            label: 'Pakkaus',
            value: selectedStair?.stepPackaging,
            timestamp: selectedStair?.stepPackaging_modifiedTimestamp,
            workStartTime: selectedStair?.stepPackaging_workStartTime,
            workEndTime: selectedStair?.stepPackaging_workEndTime,
            workBreakDuration: selectedStair?.stepPackaging_workBreakDuration,
        },
        {
            id: 'stepColoring1',
            name: 'stepColoring1',
            label: '1. värjäys',
            value: selectedStair?.stepColoring1,
            timestamp: selectedStair?.stepColoring1_modifiedTimestamp,
            workStartTime: selectedStair?.stepColoring1_workStartTime,
            workEndTime: selectedStair?.stepColoring1_workEndTime,
            workBreakDuration: selectedStair?.stepColoring1_workBreakDuration,
        },
        {
            id: 'stepColoring2',
            name: 'stepColoring2',
            label: '2. värjäys',
            value: selectedStair?.stepColoring2,
            timestamp: selectedStair?.stepColoring2_modifiedTimestamp,
            workStartTime: selectedStair?.stepColoring2_workStartTime,
            workEndTime: selectedStair?.stepColoring2_workEndTime,
            workBreakDuration: selectedStair?.stepColoring2_workBreakDuration,
        },
        {
            id: 'stepColoring3',
            name: 'stepColoring3',
            label: '3. värjäys',
            value: selectedStair?.stepColoring3,
            timestamp: selectedStair?.stepColoring3_modifiedTimestamp,
            workStartTime: selectedStair?.stepColoring3_workStartTime,
            workEndTime: selectedStair?.stepColoring3_workEndTime,
            workBreakDuration: selectedStair?.stepColoring3_workBreakDuration,
        },
    ]), [selectedStair]);

    const hasRunningOperation = useMemo(
        () => operations.some(({ workStartTime, workEndTime }) => workStartTime && !workEndTime),
        [operations],
    );

    useEffect(() => {
        if (!hasRunningOperation) return undefined;

        const interval = setInterval(() => {
            setCurrentTime(Date.now());
        }, 1000);

        return () => clearInterval(interval);
    }, [hasRunningOperation]);

    const formatTotalTime = (startTime, endTime, breakDuration = 0) => {
        if (!startTime) return '';

        const start = new Date(startTime);
        const end = endTime ? new Date(endTime) : new Date(currentTime);
        const duration = end - start - Number(breakDuration || 0);

        if (Number.isNaN(duration) || duration < 0) return '';

        const totalSeconds = Math.floor(duration / 1000);
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;

        const padded = (value) => value.toString().padStart(2, '0');

        return `${padded(hours)}:${padded(minutes)}:${padded(seconds)}`;
    };

    const renderOperation = ({ id, name, label, value, timestamp, workStartTime, workEndTime, workBreakDuration }) => {
        const hasWorkTime = Boolean(workStartTime || workEndTime);
        const isRunning = Boolean(workStartTime) && !workEndTime;
        const totalTime = formatTotalTime(workStartTime, workEndTime, workBreakDuration);
        const shouldShowTimerButton = Boolean(workStartTime) || !hasWorkTime;

        return (
            <div className="form-group operation-card" key={name}>
                <div className="label-row">
                    <label htmlFor={id}>{label}:</label>
                    {shouldShowTimerButton && (
                        <button
                            type="button"
                            className={`timer-button ${isRunning ? 'pause' : 'play'}`}
                            aria-label={isRunning ? 'Lopeta työ' : 'Aloita työ'}
                            onClick={() => handleWorkTimerToggle(name)}
                        >
                            {isRunning ? <FaPause /> : <FaPlay />}
                        </button>
                    )}
                </div>
                <input
                    type="text"
                    className="form-input"
                    id={id}
                    name={name}
                    value={value || ''}
                    onChange={handleStairModification}
                />
                <p className="modified-timestamp">
                    Suoritettu: {formatTimestamp(timestamp) || '-'}
                </p>
                <p className="total-time-label">
                    <button
                        type="button"
                        className="total-time-button"
                        aria-label={`Muokkaa työaikaa (${label})`}
                        onClick={() => onWorkTimeEdit?.({ operationName: name, label })}
                    >
                        Työaika: <span className="total-time-value">{totalTime}</span>
                    </button>
                </p>
            </div>
        );
    };

    return (
        <div className='form-columns production-grid'>
            {operations.map(renderOperation)}
        </div>
    );
};

export default OrderPageProduction;
